import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsContext } from 'next';
import { stateService } from '../../services/state';
import { StateRelation, StateWithCount } from '../../enums/state';
import { configuration } from '../../configurations/configuration';
import { ListingFilterKeys, ListingOrderBy } from '../../enums/listing';
import { OrderByDirection } from '../../enums/order-by-direction';
import { wrapper } from '../../store';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { i18n } from '../../../next-i18next.config';
import { State } from '../../entities/state';
import { County } from '../../entities/county';
import { ParsedUrlQuery } from 'querystring';
import { LayoutType } from '../../components/layout/layout';
import { Store } from 'redux';
import { AppState } from '../../store/state';
import { castArray, sumBy } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import ListingsTable from '../../components/pages/listings/table/table';
import Head from 'next/head';
import Pagination from '../../components/ui-kit/pagination/pagination';
import { useRouterListings } from '../../hooks/use-router-listings';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ListingFiltersQueryParameters } from '../../entities/listing/query-filters-parameters';
import ListingsFilters from '../../components/pages/listings/filters/filters';
import {
  loadListingsTask,
  loadStatesTask,
  MineralListingsPageActions,
  MineralListingsPageSelectors
} from '../../store/pages/mineral-listings';
import { plainToClass } from 'class-transformer';
import { DTOGroup } from '../../enums/dto-group';
import { prepareListingRouteSlug } from 'src/utils/prepare-listing-route-slug';
import ListingsBannerApproval from 'src/components/pages/listings/banner/approval/approval';
import { AuthSelectors } from 'src/store/auth';
import { ProfileSelectors } from 'src/store/profile';

const MineralListingsPage = (): JSX.Element => {
  const currentPage = useSelector(MineralListingsPageSelectors.currentPage);
  const perPage = useSelector(MineralListingsPageSelectors.perPage);
  const total = useSelector(MineralListingsPageSelectors.total);
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const profile = useSelector(ProfileSelectors.profile);

  const router = useRouter();
  const routerListings = useRouterListings();
  const dispatch = useDispatch();

  useEffect(() => {
    if (router.isReady) {
      const currentPageParams = routerListings.getCurrentPageParams();

      const order = currentPageParams.filters?.isCashFlow
        ? { order: ListingOrderBy.AVERAGE_MONTHLY_CASH_FLOW, desc: true }
        : {
          order: currentPageParams.orderBy,
          desc: currentPageParams.orderByDirection
            ? currentPageParams.orderByDirection === OrderByDirection.DESC
            : undefined
        };

      if (currentPageParams.perPage) {
        dispatch(MineralListingsPageActions.setPerPage({ perPage: currentPageParams.perPage }));
      }

      dispatch(
        MineralListingsPageActions.loadListings({
          page: currentPageParams.page,
          perPage: currentPageParams.perPage,
          ...order,
          filters: new ListingFiltersQueryParameters({
            statesIDs: currentPageParams.filters?.statesIDs ?? undefined,
            countiesIDs: currentPageParams.filters?.countiesIDs ?? undefined,
            isCashFlow: currentPageParams.filters?.isCashFlow ?? undefined,
            listings: currentPageParams.filters?.isShowSalePending
              ? ListingFilterKeys.SALE_PENDING
              : currentPageParams.filters?.listings ?? (!isAuthenticated ? ListingFilterKeys.ACTIVE : undefined),
            query: currentPageParams.filters?.query ?? undefined
          })
        })
      );
    }
  }, [router.query]);

  const handleSetPerPage = (perPage: number): void => {
    dispatch(MineralListingsPageActions.setPerPage({ perPage }));

    routerListings.push({ page: 1, perPage });
  };

  const getPageUrl = (page: number): string => {
    const currentPageParams = routerListings.getCurrentPageParams();

    return routerListings.buildPageUrl({
      page,
      orderBy: currentPageParams.orderBy,
      orderByDirection: currentPageParams.orderByDirection
    });
  };

  return (
    <>
      <Head>
        <title>Buy Mineral Rights - Browse Available Listings Now</title>
        <meta
          name='description'
          content='If you want to buy mineral rights US Mineral Exchange is the place. We work directly with sellers to help you find quality leads.'
        />
        <meta
          name='keywords'
          content='' />
        <link
          rel='canonical'
          href={`${process.env.NEXT_PUBLIC_URL}/mineral-listings`} />
      </Head>
      {isAuthenticated && profile?.isInactive && <ListingsBannerApproval />}
      <ListingsFilters />
      <Pagination
        currentPage={currentPage}
        total={total}
        perPage={perPage}
        setPerPage={handleSetPerPage}
        getPageUrl={getPageUrl}
      />
      <ListingsTable />
      <Pagination
        currentPage={currentPage}
        total={total}
        perPage={perPage}
        setPerPage={handleSetPerPage}
        getPageUrl={getPageUrl}
      />
    </>
  );
};

MineralListingsPage.layout = LayoutType.ACCOUNT;

export default MineralListingsPage;

const searchStates = async (): Promise<Array<State>> => {
  const { data } = await stateService.search({
    all: true,
    relations: [StateRelation.COUNTIES],
    withCount: [StateWithCount.LISTINGS, StateWithCount.COUNTIES_LISTINGS]
  });

  return data;
};

const getTotalPages = (total: number): number => {
  return Math.ceil(total / configuration.pagination.defaultPerPage);
};

const getPagesRange = (totalPages: number): Array<number> => {
  return Array(totalPages)
    .fill(1)
    .map((x, y) => x + y);
};

const getMainPaths = (pages: Array<number>): Array<{ params: ParsedUrlQuery }> => {
  const orderByValues = Object.values(ListingOrderBy);
  const orderByDirectionValues = Object.values(OrderByDirection);

  const paths = [];

  for (const page of pages) {
    paths.push({ params: { slug: [page.toString()] } });

    for (const orderByValue of orderByValues) {
      paths.push({ params: { slug: [page.toString(), 'sort', orderByValue] } });

      for (const orderByDirectionValue of orderByDirectionValues) {
        paths.push({ params: { slug: [page.toString(), 'sort', orderByValue, orderByDirectionValue] } });
      }
    }
  }

  return paths;
};

const getNestedPaths = (pages: Array<number>, state?: State, county?: County): Array<{ params: ParsedUrlQuery }> => {
  const orderByValues = Object.values(ListingOrderBy);
  const orderByDirectionValues = Object.values(OrderByDirection);

  const paths = [];

  for (const page of pages) {
    for (const orderByValue of orderByValues) {
      for (const orderByDirectionValue of orderByDirectionValues) {
        const slug = [page.toString(), 'sort', orderByValue, orderByDirectionValue];

        if (state) {
          slug.push(state.nameLowerCase);
        }

        if (county) {
          slug.push(county.nameInKebabCase);
        }

        paths.push({ params: { slug } });
      }
    }
  }

  return paths;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const result: GetStaticPathsResult = {
    paths: [],
    fallback: 'blocking'
  };

  if (!('GENERATE_STATIC_PATHS' in process.env)) {
    return result;
  }

  const states = await searchStates();
  const listingsTotal = sumBy(states, 'listingsCount');
  const totalPages = getTotalPages(listingsTotal);

  const rangeTotalPages = getPagesRange(totalPages);

  result.paths = [...getMainPaths(rangeTotalPages)];

  for (const state of states) {
    const stateTotalPages = getTotalPages(state.listingsCount);
    const stateRangeTotalPages = getPagesRange(stateTotalPages);

    result.paths = [...result.paths, ...getNestedPaths(stateRangeTotalPages, state)];

    for (const county of state.counties) {
      const countyTotalPages = getTotalPages(county.listingsCount);
      const countyRangeTotalPages = getPagesRange(countyTotalPages);

      result.paths = [...result.paths, ...getNestedPaths(countyRangeTotalPages, state, county)];
    }
  }

  return result;
};

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(
  (store: Store<AppState>) => async ({ locale, params, preview }: GetStaticPropsContext) => {
    if (preview) {
      return {
        redirect: {
          permanent: false,
          destination: configuration.redirectPaths.checkPreview
        }
      };
    }

    let storeState = store.getState();

    if (!storeState.pages.mineralListings.states.length) {
      store.dispatch(MineralListingsPageActions.loadStatesForServer());
      await loadStatesTask?.toPromise();

      storeState = store.getState();
    }

    const [page, state, county, sort, direction] = prepareListingRouteSlug(castArray(params?.slug));

    let stateID = undefined;
    let countyID = undefined;
    if (state) {
      const storedStates = plainToClass(State, storeState.pages.mineralListings.states, { groups: [DTOGroup.MAIN] });
      const storedState = storedStates.find((storedState) => storedState.nameLowerCase === state);
      stateID = storedState?.id;

      if (storedState && county) {
        const storedCounty = storedState.counties.find((storedCounty) => storedCounty.nameInKebabCase === county);
        countyID = storedCounty?.id;
      }
    }

    store.dispatch(
      MineralListingsPageActions.loadListingsForServer({
        page: Number(page) || 1,
        stateID,
        countyID,
        order:
            sort && Object.values(ListingOrderBy)
              .includes(sort as ListingOrderBy)
              ? (sort as ListingOrderBy)
              : undefined,
        desc: direction ? direction === OrderByDirection.DESC : undefined
      })
    );

    await loadListingsTask?.toPromise();

    return {
      props: {
        ...(await serverSideTranslations(locale ?? i18n.defaultLocale, [
          'common',
          'components:layout',
          'components:pages:listings:filters',
          'components:ui-kit:pagination',
          'components:pages:listings:table',
          'components:pages:listings:banner-approval'
        ]))
      },
      revalidate: configuration.revalidation.timeSec
    };
  }
);
